import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useState, useCallback, useMemo, useRef, useEffect, type ReactNode, type CSSProperties } from "react";
import { AlertCircle, Bot, ChevronDown, ChevronRight, ChevronUp, Flag, Info, CircleDashedCheck, List, Loader2, MessageCircle, MessageSquare, Phone, RefreshCw, ScanSearch, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { AddOn, VehicleDetails, FinanceFigures, FinanceFiguresInput, QuoteSnapshotPayload, UnderwritingActivity, UnderwritingNavOrigin, PrepareApplicationCustomer, PrepareApplicationEmployment, NoteModel, QuoteLenderRef, TranscriptChannel, TranscriptEntry, TranscriptSegment, UWQueueActionPayload } from "@/models/underwriting";
import { ClaimConfirmDialog } from "@/components/UnderwritingDashboard/ClaimConfirmDialog";
import {
  QueueActionDialog,
  type AllocateActionPayload,
  type DeadActionPayload,
} from "@/components/UnderwritingDashboard/QueueActionDialog";
import { QuoteDetailsDialog } from "@/components/UnderwritingDashboard/QuoteDetailsDialog";
import { CreditCheckDialog } from "@/components/UnderwritingDashboard/CreditCheckDialog";
import { AdditionalQuestionsPage } from "@/components/UnderwritingDashboard/AdditionalQuestionsPage";
import { AddVehicleModal } from "@/components/UnderwritingDashboard/AddVehicleModal";
import { ChangeDealerModal } from "@/components/UnderwritingDashboard/ChangeDealerModal";
import { ChangeFinanceModal } from "@/components/UnderwritingDashboard/ChangeFinanceModal";
import { ChangeExtrasModal, SelectedExtra } from "@/components/UnderwritingDashboard/ChangeExtrasModal";
import { EmploymentHistory } from "@/components/UnderwritingDashboard/EmploymentHistory";
import { CustomerDetailsEditor } from "@/components/UnderwritingDashboard/CustomerDetailsEditor";
import { FormattedHtmlText } from "@/components/UnderwritingDashboard/FormattedHtmlText";
import { MessagesTab } from "@/components/UnderwritingDashboard/MessagesTab";
import { LenderDecisionHistoryDialog } from "@/components/UnderwritingDashboard/LenderDecisionHistoryDialog";
import { VehicleDetailModel, VehicleExtraFilterParams, VehicleTypes } from "@/models/vehicle";
import { underwritingService } from "@/services/UnderwritingService";
import { applicationService } from "@/services/ApplicationService";
import { vehicleService } from "@/services/VehicleService";
import { dealerService } from "@/services/DealerService";
import type { SelectedDealer } from "@/types/dealer";
import { mapCustomer, mapProposals, mapVehicle, mapDealer, resolveAdminFee } from "@/helpers/pricingMappers";
import { proposalService } from "@/services/ProposalService";
import { useAppData } from "@/context/AppDataContext";
import { useApi, type UseApiResult } from "@/hooks/useApi";
import { vehicleFinanceFromHistory } from "@/helpers/vehicleFinanceFromHistory";
import { mapMonthlyPriceToQuoteRequest, buildLoanProposalRequest } from "@/helpers/quoteProposal";
import { compareQuotes, type QuoteSortBy } from "@/helpers/quoteSort";
import { getDisplayStatus, isQuoteProcessing, isQuoteProvisionalAcceptance } from "@/helpers/quoteStatus";
import { toast } from "sonner";
import { calculate } from "@cf247/pricing/dist/calculator/calculator";
import { FinanceType, AncillaryProductLenderStanceType, FinanceCompany, AffiliateType } from "@cf247/pricing/dist/enums";
import type { MonthlyPrice, Vehicle, Dealer } from "@cf247/pricing/dist/types";

function formatCurrency(n: number | undefined | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n);
}

function vehicleDetailsFromSnapshot(v: Vehicle | undefined): VehicleDetails {
  return {
    makeModel: v ? `${v.make} ${v.model}${v.trim ? " " + v.trim : ""}` : "—",
    registration: v?.vrm ?? "—",
    colour: v?.colour ?? "—",
    mileage: v?.mileage != null ? `${v.mileage.toLocaleString()} mi` : "—",
    cashPrice: formatCurrency(v?.price),
  };
}

/** Maps a VRM-search result onto the Summary tab's vehicle fields. */
function vehicleDetailsFromModel(v: VehicleDetailModel): VehicleDetails {
  return {
    makeModel: [v.make, v.model, v.trim].filter(Boolean).join(" ") || "—",
    registration: v.regNumber || "—",
    vehicleType: VehicleTypes[v.vehicleType ?? VehicleTypes.Car],
    colour: v.colour || "—",
    mileage: v.mileage != null ? `${v.mileage.toLocaleString()} mi` : "—",
    cashPrice: formatCurrency(v.price),
  };
}

/** Formats the editable finance inputs back into the display figures. */
function financeFiguresFromInput(f: FinanceFiguresInput): FinanceFigures {
  return {
    cashPrice: formatCurrency(f.cashPrice),
    deposit: formatCurrency(f.deposit),
    partExchange: formatCurrency(f.partExchange),
    settlement: formatCurrency(f.settlement),
    annualMileage: `${f.annualMileage.toLocaleString()} mi/yr`,
    existingFinanceCo: null,
    loanAmount: formatCurrency(f.loanAmount),
    term: `${f.term} months`,
  };
}

function financeFiguresFromSnapshot(
  v: Vehicle | undefined,
  deposit: number,
  pxValue: number,
  pxSettlement: number,
  annualMileage: number,
  maxTerm: number,
  dealer: Dealer | undefined
): FinanceFigures {
  // Mirror the pricing library's balanceToFinance: the dealer admin fee is added
  // to the loan when it's set to be included in finance. Finance-stance extras are
  // not represented here — the snapshot stores only product ids/stances (no price),
  // so their finance value is resolved per-lender when quotes are generated.
  const includedAdminFee =
    dealer?.adminFee?.includedInFinance ? dealer.adminFee.amount ?? 0 : 0;
  const loanAmount =
    (v?.price ?? 0) + includedAdminFee - deposit - pxValue + pxSettlement;
  return {
    cashPrice: formatCurrency(v?.price),
    deposit: formatCurrency(deposit),
    partExchange: formatCurrency(pxValue),
    settlement: formatCurrency(pxSettlement),
    annualMileage: `${annualMileage.toLocaleString()} mi/yr`,
    existingFinanceCo: null,
    loanAmount: formatCurrency(loanAmount > 0 ? loanAmount : 0),
    term: `${maxTerm} months`,
  };
}

// Helpers

/**
 * Builds the Add-ons list from the snapshot's included extras. Product names and
 * prices are only exposed on the generated quotes' `extrasPrices`, so they resolve
 * once quotes have been generated; before then the product id is shown as a fallback.
 */
function addOnsFromSnapshot(
  included: { productId: number; stance: AncillaryProductLenderStanceType }[] | null | undefined,
  quotes: MonthlyPrice[] | null
): AddOn[] {
  if (!included?.length) return [];
  const lookup = new Map<number, { name: string; price: number }>();
  for (const quote of quotes ?? []) {
    for (const extra of quote.extrasPrices ?? []) {
      if (!lookup.has(extra.productId)) {
        lookup.set(extra.productId, { name: extra.productName, price: extra.productPrice });
      }
    }
  }
  const addOns: AddOn[] = [];
  for (const inc of included) {
    const info = lookup.get(inc.productId);
    // Product name/price only exist on generated quotes; skip until they resolve.
    if (!info) continue;
    addOns.push({
      name: info.name,
      price: formatCurrency(info.price),
      status: inc.stance === AncillaryProductLenderStanceType.Finance ? "In Finance" : "Not Included",
    });
  }
  return addOns;
}

function formatTimeAgo(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

const LEAVE_WITHOUT_ACTION_MESSAGE =
  "You've proposed a quote to a lender but haven't recorded a decision. Are you sure you want to leave without submitting an action?";

/** Maximum time an application can stay open without an action before the user is sent back. */
const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000;

// Sub-components

function Breadcrumb({
  customerName,
  originLabel,
  onDashboardClick,
}: {
  customerName: string;
  originLabel: string;
  onDashboardClick: () => void;
}) {
  return (
    <nav className="flex items-center gap-1 text-sm text-muted-foreground">
      <button
        onClick={onDashboardClick}
        className="hover:text-foreground transition-colors"
      >
        {originLabel}
      </button>
      <ChevronRight className="size-3.5 shrink-0" />
      <span className="font-medium text-foreground">{customerName}</span>
    </nav>
  );
}

/** A plain titled card used for the Overview and Quotes panels on the left column. */
function SectionCard({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border border-border bg-card shadow-sm", className)}>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-[12px] font-semibold text-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Header claim control — replaces the standalone availability banner. */
function HeaderClaimButton({
  lockedBy,
  lockedByAgentId,
  lockedOn,
  currentUserId,
  disabled,
  onClaim,
  onUnclaim,
}: {
  lockedBy: string | null;
  lockedByAgentId: number | null;
  lockedOn: string | null;
  currentUserId: number | null;
  disabled: boolean;
  onClaim?: () => Promise<void>;
  onUnclaim?: () => Promise<void>;
}) {
  const isMine = lockedByAgentId !== null && lockedByAgentId === currentUserId;
  const isOther = lockedBy !== null && !isMine;
  const isStale = lockedOn != null && Date.now() - new Date(lockedOn).getTime() > 20 * 60 * 1000;

  if (isMine) {
    return (
      <Button
        disabled={disabled}
        variant="outline"
        className="text-xs px-2.5 py-2 h-auto"
        onClick={onUnclaim}
      >
        Unclaim
      </Button>
    );
  }

  if (isOther) {
    if (!isStale) return null;
    return (
      <ClaimConfirmDialog
        trigger={
          <Button disabled={disabled} variant="outline" className="border-amber-400 text-slate-700 hover:bg-amber-100 text-xs px-2.5 py-1.5 h-auto">
            Claim Over
          </Button>
        }
        title="Override Claim Application"
        description={`This request has been held by ${lockedBy} for over 20 minutes. Do you want to take over and assign it to yourself?`}
        confirmLabel="Take Over"
        onConfirm={onClaim}
      />
    );
  }

  return (
    <ClaimConfirmDialog
      trigger={<Button disabled={disabled} variant="outline" className="text-xs px-2.5 py-2 h-auto">Claim</Button>}
      onConfirm={onClaim}
      title="Claim Application"
      description="Please confirm you want to claim this application. It will be assigned to you and removed from the available queue."
    />
  );
}

// Application Notes

function NoteItem({ note }: { note: NoteModel }) {
  const date = new Date(note.commentDate).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const name = note.userName ?? "System";

  return (
    <div className="flex flex-col gap-1">
      <FormattedHtmlText
        as="p"
        html={note.comment}
        className="min-w-0 [overflow-wrap:anywhere] text-[11px] leading-snug text-foreground [&_a]:underline [&_a]:underline-offset-2"
      />
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {note.highImportance && <Flag className="size-3 shrink-0 text-amber-500" />}
        <span className="font-medium text-foreground/80">{name}</span>
        <span>·</span>
        <span>{date}</span>
        {note.department && <span>({note.department})</span>}
      </div>
    </div>
  );
}

function NotesTab({
  applicationId,
  currentUserName,
  currentUserId,
  onNoteAdded,
}: {
  applicationId: number | null;
  currentUserName: string | null;
  currentUserId: number | null;
  onNoteAdded: () => void;
}) {
  const notesResult = useApi(
    (signal) => underwritingService.getNotes(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  const importantNotes = useMemo(
    () =>
      (notesResult.data ?? [])
        .filter((n) => n.highImportance)
        .sort((a, b) => (a.commentDate < b.commentDate ? 1 : -1)),
    [notesResult.data]
  );
  const otherNotes = useMemo(
    () =>
      (notesResult.data ?? [])
        .filter((n) => !n.highImportance)
        .sort((a, b) => (a.commentDate < b.commentDate ? 1 : -1)),
    [notesResult.data]
  );

  const [comment, setComment] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [sendToDealer, setSendToDealer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const listTopRef = useRef<HTMLDivElement>(null);

  // Scroll to top of list when notes refresh after adding
  useEffect(() => {
    if (!notesResult.loading && notesResult.data) {
      listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [notesResult.data, notesResult.loading]);

  const handleAddNote = async () => {
    if (!comment.trim() || applicationId == null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await underwritingService.addNote({
        applicationId,
        comment: comment.trim(),
        createdBy: currentUserName ?? "",
        isImportant,
        isPublic: sendToDealer,
        isUserComment: true,
        userId: currentUserId ?? 0,
      });
      setComment("");
      setIsImportant(false);
      setSendToDealer(false);
      notesResult.refetch();
      onNoteAdded();
    } catch {
      setSubmitError("Failed to add note. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable notes list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div ref={listTopRef} />

        {notesResult.loading && (
          <div className="flex items-center gap-2 px-4 py-4 text-[11px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading notes…
          </div>
        )}

        {notesResult.error && (
          <p className="px-4 py-3 text-[11px] text-rose-600">{notesResult.error}</p>
        )}

        {!notesResult.loading && !notesResult.error && (
          <>
            {importantNotes.length > 0 && (
              <div className="bg-amber-50 px-4 py-3">
                <p className="mb-2 text-[12px] font-semibold text-foreground">Important Notes</p>
                <div className="flex flex-col gap-3">
                  {importantNotes.map((note, i) => <NoteItem key={i} note={note} />)}
                </div>
              </div>
            )}

            <div className="px-4 py-3">
              <p className="mb-2 text-[12px] font-semibold text-foreground">All Notes</p>
              {otherNotes.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {otherNotes.map((note, i) => <NoteItem key={i} note={note} />)}
                </div>
              ) : (
                !importantNotes.length && (
                  <p className="py-4 text-[11px] text-muted-foreground">No note(s) available</p>
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Add note */}
      <div className="shrink-0 border-t border-border p-3">
        <div className="rounded-lg border border-border focus-within:ring-2 focus-within:ring-slate-600">
          <textarea
            className="w-full resize-none rounded-t-lg bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            rows={3}
            placeholder="Add a note…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="flex items-end justify-between gap-3 px-2 py-2">
            <div className="flex flex-col items-start gap-2">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
                <Checkbox
                  checked={isImportant}
                  onCheckedChange={(value) => setIsImportant(value === true)}
                  disabled={submitting}
                />
                Important
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
                <Checkbox
                  checked={sendToDealer}
                  onCheckedChange={(value) => setSendToDealer(value === true)}
                  disabled={submitting}
                />
                Send to dealer
              </label>
            </div>
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleAddNote}
              disabled={submitting || !comment.trim()}
            >
              {submitting ? <Loader2 className="size-3.5 animate-spin mr-1" /> : null}
              Add note
            </Button>
          </div>
        </div>
        {submitError && <p className="mt-1.5 text-xs text-rose-600">{submitError}</p>}
      </div>
    </div>
  );
}

// Application History

/** "14 Jul, 15:02" — day, short month, then 24h time. */
function formatHistoryDate(isoString: string): string {
  const d = new Date(isoString);
  const date = d.toLocaleString("en-GB", { day: "2-digit", month: "short" });
  const time = d.toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date}, ${time}`;
}

function HistoryTab({ historyResult }: { historyResult: UseApiResult<NoteModel[]> }) {
  // Newest first, so the timeline reads top-to-bottom in chronological order.
  const entries = useMemo(
    () =>
      (historyResult.data ?? [])
        .slice()
        .sort((a, b) => (a.commentDate < b.commentDate ? 1 : -1)),
    [historyResult.data]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4">
        {historyResult.loading && (
          <div className="flex items-center gap-2 py-4 text-[11px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading history…
          </div>
        )}

        {historyResult.error && (
          <p className="py-3 text-[11px] text-rose-600">{historyResult.error}</p>
        )}

        {!historyResult.loading && !historyResult.error && (
          entries.length > 0 ? (
            <ol className="flex flex-col">
              {entries.map((entry, i) => (
                <li key={i} className="relative flex gap-3 pb-5 last:pb-0">
                  {i < entries.length - 1 && (
                    <span className="absolute left-[5px] top-3 h-full w-px bg-border" aria-hidden />
                  )}
                  <span className="mt-1 size-2.5 shrink-0 rounded-full border-2 border-slate-300 bg-background" />
                  <div className="flex min-w-0 flex-col gap-0.5 break-words [overflow-wrap:anywhere]">
                    <FormattedHtmlText
                      as="p"
                      html={entry.comment}
                      className="text-[12px] leading-snug text-foreground [&_a]:underline [&_a]:underline-offset-2"
                    />
                    <span className="text-[11px] text-muted-foreground">
                      {formatHistoryDate(entry.commentDate)}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="py-4 text-[11px] text-muted-foreground">No history available</p>
          )
        )}
      </div>
    </div>
  );
}

// Transcripts

/** "15 Jul 2026, 14:02" — day, short month, year, then 24h time. */
function formatTranscriptDate(isoString: string): string {
  const d = new Date(isoString);
  const date = d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date}, ${time}`;
}

const TRANSCRIPT_ICONS: Record<TranscriptChannel, typeof Phone> = {
  Call: Phone,
  WhatsApp: MessageCircle,
  SMS: MessageSquare,
  ChatBot: Bot,
};

function TranscriptSegments({ segments }: { segments: TranscriptSegment[] }) {
  if (segments.length === 0) {
    return <p className="text-[11px] text-muted-foreground">No transcript available.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      {segments.map((seg, i) => (
        <div key={i} className="flex flex-col gap-0.5">
          <span className="text-[11px] font-semibold text-foreground/80">{seg.speaker}</span>
          <p className="text-[12px] leading-snug text-foreground">{seg.content}</p>
        </div>
      ))}
    </div>
  );
}

function TranscriptRow({ entry }: { entry: TranscriptEntry }) {
  const [open, setOpen] = useState(false);
  const [segments, setSegments] = useState<TranscriptSegment[] | null>(entry.segments);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const Icon = TRANSCRIPT_ICONS[entry.channel];

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    // Call transcripts are fetched lazily the first time a row is expanded.
    if (next && segments == null && entry.recordingId) {
      setLoading(true);
      setError(null);
      try {
        const result = await underwritingService.getCallTranscript(entry.recordingId);
        setSegments(result);
      } catch {
        setError("Failed to load transcript. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
      >
        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-x-2">
            <span className="text-[13px] font-semibold text-foreground">{entry.title}</span>
            <span className="text-[11px] text-muted-foreground">{formatTranscriptDate(entry.date)}</span>
          </div>
          {entry.summary && !open && (
            <p className="line-clamp-2 text-[12px] leading-snug text-muted-foreground">
              {entry.summary}
            </p>
          )}
        </div>
        <ChevronRight
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90"
          )}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 pl-11">
          {loading && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading transcript…
            </div>
          )}
          {error && <p className="text-[11px] text-rose-600">{error}</p>}
          {!loading && !error && segments && <TranscriptSegments segments={segments} />}
        </div>
      )}
    </div>
  );
}

function TranscriptsTab({ applicationId }: { applicationId: number | null }) {
  const transcriptsResult = useApi(
    (signal) => underwritingService.getTranscripts(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  const entries = transcriptsResult.data ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {transcriptsResult.loading && (
          <div className="flex items-center gap-2 px-4 py-4 text-[11px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading transcripts…
          </div>
        )}

        {transcriptsResult.error && (
          <p className="px-4 py-3 text-[11px] text-rose-600">{transcriptsResult.error}</p>
        )}

        {!transcriptsResult.loading && !transcriptsResult.error && (
          entries.length > 0 ? (
            entries.map((entry) => <TranscriptRow key={entry.id} entry={entry} />)
          ) : (
            <p className="px-4 py-4 text-[11px] text-muted-foreground">No transcripts available</p>
          )
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-h-[46px] grid-cols-[132px_1fr] items-center gap-3 px-4 py-2">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div className="text-[11px] text-foreground">{children}</div>
    </div>
  );
}

/** Application status pill — neutral styling, shows the workscreen status label. */
function AppStatusPill({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-4 py-0.5 text-xs font-medium text-slate-700">
      {label}
    </span>
  );
}

/** The two-column Overview card content on the left column. */
function OverviewContent({
  details,
  loading,
  quotesCount,
  activity,
  amountToFinance,
}: {
  details: PrepareApplicationCustomer | null;
  loading: boolean;
  quotesCount: number;
  activity: UnderwritingActivity;
  amountToFinance?: number | null;
}) {
  const applicationId = details?.applicationId ?? activity.applicationId;
  const customerName = details?.name ?? activity.customerName ?? "—";
  const statusLabel = details?.applicationStatusName ?? details?.applicationStatus ?? null;

  const applicationDate = details?.applicationDate
    ? new Date(details.applicationDate).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "—";

  if (loading && !details) {
    return (
      <div className="flex items-center gap-2 px-4 py-6 text-[11px] text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading application details…
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-border">
      <div className="divide-y divide-border">
        <DetailRow label="Application">
          <span>{applicationId}</span>
        </DetailRow>
        <DetailRow label="Customer">{customerName}</DetailRow>
        <DetailRow label="Status">
          {statusLabel ? <AppStatusPill label={statusLabel} /> : "—"}
        </DetailRow>
        <DetailRow label="Application Date">{applicationDate}</DetailRow>
        <DetailRow label="Number of Quotes">{quotesCount}</DetailRow>
      </div>
      <div className="divide-y divide-border">
        {/* Extra details below are provisional pending PO confirmation. */}
        <DetailRow label="Amount to Finance">
          {formatCurrency(amountToFinance ?? details?.amountToFinance)}
        </DetailRow>
        <DetailRow label="Allocated Agent">{details?.allocationData?.allocatedAgentName || "—"}</DetailRow>
        <DetailRow label="Department">{details?.allocationData?.allocatedDepartmentName || "—"}</DetailRow>
        <DetailRow label="Net Monthly Income">{formatCurrency(details?.bankDetails?.netMonthlyIncome)}</DetailRow>
        <DetailRow label="Vulnerable Customer">
          {details?.hasVulnerableCustomerReported ? "Yes" : "No"}
        </DetailRow>
      </div>
    </div>
  );
}

function SummaryField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="text-right text-[11px] text-foreground">{value}</span>
    </div>
  );
}

/** The Summary tab content on the right column: Vehicle, Finance and Fees/Add-ons. */
function SummaryTab({
  vehicle,
  dealer,
  dealerLoading,
  finance,
  addOns,
  addOnsPending,
  addOnsLoading,
  hasVehicle,
  canEdit,
  onEditVehicle,
  onEditDealer,
  onEditFinance,
  onEditExtras,
}: {
  vehicle: VehicleDetails;
  dealer: SelectedDealer | null;
  dealerLoading: boolean;
  finance: FinanceFigures;
  addOns: AddOn[];
  addOnsPending: boolean;
  addOnsLoading: boolean;
  hasVehicle: boolean;
  canEdit: boolean;
  onEditVehicle?: () => void;
  onEditDealer?: () => void;
  onEditFinance?: () => void;
  onEditExtras?: () => void;
}) {
  return (
    <div className="flex flex-col gap-6 px-4 py-4">
      <section>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold text-foreground">Vehicle</h3>
          {onEditVehicle && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onEditVehicle}
              disabled={!canEdit}
              title={!canEdit ? "Claim this application to edit" : undefined}
            >
              {hasVehicle ? "Change Vehicle" : "Add Vehicle"}
            </Button>
          )}
        </div>
        <SummaryField label="VRM" value={vehicle.registration} />
        <SummaryField label="Make & Model" value={vehicle.makeModel} />
        <SummaryField label="Vehicle Type" value={vehicle.vehicleType ?? "—"} />
        <SummaryField label="Colour" value={vehicle.colour} />
        <SummaryField label="Mileage" value={vehicle.mileage} />
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold text-foreground">Dealer</h3>
          {onEditDealer && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onEditDealer}
              disabled={!canEdit || dealerLoading}
              title={!canEdit ? "Claim this application to edit" : undefined}
            >
              {dealer ? "Change Dealer" : "Add Dealer"}
            </Button>
          )}
        </div>
        {dealerLoading ? (
          <div className="flex items-center gap-2 py-2 text-[11px] text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Loading dealer…
          </div>
        ) : (
          <>
            <SummaryField label="Name" value={dealer?.dealerName ?? "—"} />
            <SummaryField label="Postcode" value={dealer?.postcode ?? "—"} />
            <SummaryField label="Dealer ID" value={dealer?.dealerId ?? "—"} />
          </>
        )}
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold text-foreground">Finance</h3>
          {onEditFinance && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onEditFinance}
              disabled={!canEdit}
              title={!canEdit ? "Claim this application to edit" : undefined}
            >
              Update Figures
            </Button>
          )}
        </div>
        <SummaryField label="Cash Price" value={finance.cashPrice} />
        <SummaryField label="Deposit" value={finance.deposit} />
        <SummaryField label="Part Ex Value" value={finance.partExchange ?? "—"} />
        <SummaryField label="Part Ex Settlement" value={finance.settlement ?? "—"} />
        <SummaryField label="Loan Amount" value={finance.loanAmount} />
        <SummaryField label="Term" value={finance.term} />
        <SummaryField label="Annual Mileage" value={finance.annualMileage} />
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold text-foreground">Fees and Add-ons</h3>
          {onEditExtras && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              disabled={!hasVehicle || !canEdit}
              onClick={onEditExtras}
              title={!canEdit ? "Claim this application to edit" : undefined}
            >
              Update
            </Button>
          )}
        </div>
        {addOns.length === 0 ? (
          addOnsLoading && addOnsPending ? (
            <div className="flex items-center gap-2 py-2 text-[11px] text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Loading prices…
            </div>
          ) : (
            <p className="py-2 text-[11px] text-muted-foreground">
              {addOnsPending ? "Available after generating quotes." : "No fees or add-ons"}
            </p>
          )
        ) : (
          addOns.map((a) => (
            <div
              key={a.name}
              className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0"
            >
              <span className="text-[11px] text-muted-foreground">{a.name}</span>
              <span className="text-right">
                <span className="block text-[11px] text-foreground">{a.price}</span>
                <span className="text-[11px] text-muted-foreground">
                  {a.status === "In Finance" ? "Included in Finance" : "Cash"}
                </span>
                {a.soldBy && (
                  <span className="block text-[11px] text-muted-foreground">Sold by {a.soldBy}</span>
                )}
              </span>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

// Quotes table

const GBP = (n: number | null | undefined) =>
  n != null ? `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";

const PCT = (n: number | null | undefined) =>
  n != null ? `${n.toFixed(1)}%` : "—";

function FinanceTypeBadge({ type }: { type: number | null | undefined }) {
  if (!type) return null;
  const label = type === FinanceType.PCP ? "PCP" : "HP";
  const cls = "border-slate-300 bg-slate-50 text-slate-700";
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", cls)}>
      {label}
    </span>
  );
}

/** Unique key for a quote row — mirrors Flex's key which includes term to handle multi-term results. */
const quoteKey = (q: Pick<MonthlyPrice, "lenderId" | "financeType" | "term">) =>
  `${q.lenderId}-${q.financeType}-${q.term ?? 0}`;

/** Statuses that cannot be proposed to a lender. */
const NON_PROPOSABLE_STATUSES = ["Failed Send Rules", "Failed Rules", "Declined", "Expired", "Error"];

/** Ordering of the "Other Decisions" section, mirroring the pricing UI's STATUS_PRIORITY. */
const STATUS_PRIORITY: Record<string, number> = {
  Accepted: 1,
  "Provisional Acceptance": 2,
  "Conditionally Accepted": 2,
  "Failed Send Rules": 3,
  "Failed Rules": 3,
  Error: 3,
  Declined: 4,
  "Manual Processing": 5,
  Processing: 5,
  "More Info": 5,
  Expired: 5,
};

/** Buckets a quote is sorted into within the quotes table. */
type QuoteGroup = "main" | "processing" | "other" | "failed" | "declined";

const SUCCESS_STATUSES = new Set(["Accepted", "Provisional Acceptance", "Conditionally Accepted"]);
const PROCESSING_STATUSES = new Set(["Processing", "Manual Processing"]);
const FAILED_STATUSES = new Set(["Failed Rules", "Failed Send Rules", "Error"]);

const isProposableStatus = (s: string) => !NON_PROPOSABLE_STATUSES.includes(s);

/** A quote that can be proposed to a lender (not failed, and a proposable status). */
const isProposableQuote = (q: MonthlyPrice) => !q.failed && isProposableStatus(getDisplayStatus(q));

/** Sort for the Failed Rules / Declined sections: proposable first, then by status priority, then by metric. */
function compareOtherDecisions(a: MonthlyPrice, b: MonthlyPrice, sortBy: QuoteSortBy): number {
  const proposableA = isProposableQuote(a);
  const proposableB = isProposableQuote(b);
  if (proposableA !== proposableB) return proposableA ? -1 : 1;
  const priorityA = STATUS_PRIORITY[getDisplayStatus(a)] ?? 99;
  const priorityB = STATUS_PRIORITY[getDisplayStatus(b)] ?? 99;
  if (priorityA !== priorityB) return priorityA - priorityB;
  return compareQuotes(a, b, sortBy);
}

/** A card whose body can be collapsed by clicking the header (used for Failed Rules / Declined). */
function CollapsibleCard({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center gap-2 px-4 py-3 text-left",
          open && "border-b border-border"
        )}
      >
        <h2 className="text-[12px] font-semibold text-foreground">{title}</h2>
        {count != null && <span className="text-[11px] text-muted-foreground">({count})</span>}
        <ChevronDown
          className={cn("size-4 text-muted-foreground transition-transform", !open && "-rotate-90")}
        />
      </button>
      {open && children}
    </div>
  );
}

/** Lender / Product / Reason table for the Failed Rules and Declined sections. */
function DecisionTable({ rows, variant }: { rows: MonthlyPrice[]; variant: "failed" | "declined" }) {
  const th = "px-3 py-2 text-left text-[12px] font-semibold text-muted-foreground whitespace-nowrap";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <th className={th}>Lender</th>
            <th className={th}>Product</th>
            <th className={th}>Reason</th>
          </tr>
        </thead>
        <tbody className="text-[12px]">
          {rows.map((q) => {
            const reason = q.failReasons?.length
              ? q.failReasons.map((r) => r.message).join("; ")
              : getDisplayStatus(q);
            return (
              <tr key={quoteKey(q)} className="border-b border-border last:border-0">
                <td className="px-3 py-2.5 font-medium text-foreground">
                  <span className="block max-w-[150px] truncate" title={q.lenderName ?? undefined}>
                    {q.lenderName ?? "—"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <FinanceTypeBadge type={q.financeType} />
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-start gap-2 text-muted-foreground">
                    {variant === "failed" ? (
                      <AlertCircle className="mt-px size-4 shrink-0 text-amber-500" />
                    ) : (
                      <XCircle className="mt-px size-4 shrink-0 text-rose-500" />
                    )}
                    <span>{reason}</span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function QuotesTable({
  quotes,
  requestedLender,
  bestLender,
  requestedDeposit,
  onPropose,
  onDetails,
  proposingQuoteKey,
  canPropose,
  headerAction,
  errorMessage,
}: {
  quotes: MonthlyPrice[];
  requestedLender?: QuoteLenderRef | null;
  bestLender?: QuoteLenderRef | null;
  requestedDeposit: number;
  onPropose: (quote: MonthlyPrice) => void;
  onDetails: (quote: MonthlyPrice) => void;
  proposingQuoteKey: string | null;
  canPropose: boolean;
  headerAction?: ReactNode;
  errorMessage?: string | null;
}) {
  const [sortBy, setSortBy] = useState<QuoteSortBy>("Monthly Payment");

  const groupOf = (q: MonthlyPrice): QuoteGroup => {
    const status = getDisplayStatus(q);
    const depositExceeds = (q.deposit ?? 0) > requestedDeposit;
    if (status === "Declined") return "declined";
    if (q.failed || FAILED_STATUSES.has(status)) return "failed";
    // Match Flex: deposit within limit and lender accepted
    if (!depositExceeds && SUCCESS_STATUSES.has(status)) return "main";
    if (PROCESSING_STATUSES.has(status)) return "processing";
    return "other";
  };

  const byGroup: Record<QuoteGroup, MonthlyPrice[]> = {
    main: [],
    processing: [],
    other: [],
    failed: [],
    declined: [],
  };
  for (const q of quotes) byGroup[groupOf(q)].push(q);

  const mainQuotes = byGroup.main.sort((a, b) => compareQuotes(a, b, sortBy));
  // Processing and other approvals render below the main quotes, in that order.
  const belowMainQuotes = [
    ...byGroup.processing.sort((a, b) => compareQuotes(a, b, sortBy)),
    ...byGroup.other.sort((a, b) => compareQuotes(a, b, sortBy)),
  ];
  const failedRules = byGroup.failed.sort((a, b) => compareOtherDecisions(a, b, sortBy));
  const declined = byGroup.declined.sort((a, b) => compareOtherDecisions(a, b, sortBy));

  // The snapshot may store financeType as a string ("HP"/"PCP") while quotes use
  // the numeric FinanceType enum. Normalise both to the enum before comparing.
  const normaliseFinanceType = (ft: FinanceType | string | null | undefined): FinanceType | null => {
    if (ft == null) return null;
    if (typeof ft === "number") return ft;
    const upper = ft.toUpperCase();
    if (upper === "HP") return FinanceType.HP;
    if (upper === "PCP") return FinanceType.PCP;
    return null;
  };

  const matchesRef = (q: MonthlyPrice, ref?: QuoteLenderRef | null) =>
    ref != null &&
    q.lenderId === ref.lenderId &&
    normaliseFinanceType(q.financeType) === normaliseFinanceType(ref.financeType);

  const th = "px-2 py-2 text-left text-[12px] font-semibold text-muted-foreground whitespace-nowrap";
  const SortHeader = ({ label, col }: { label: string; col: QuoteSortBy }) => (
    <th className={cn(th, "cursor-pointer select-none")} onClick={() => setSortBy(col)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortBy === col && <ChevronUp className="size-3" />}
      </span>
    </th>
  );

  const renderRow = (q: MonthlyPrice, extraCls?: string) => {
    const isRequested = matchesRef(q, requestedLender);
    const isBest = matchesRef(q, bestLender);
    const depositExceeds = (q.deposit ?? 0) > requestedDeposit;
    const isProcessing = isQuoteProcessing(q);
    const isProvisionalAcceptance = isQuoteProvisionalAcceptance(q);
    const pxEquity = (q.pxValue ?? 0) - (q.pxSettlement ?? 0);
    const rowCls = cn(
      "border-b border-border text-[11px] transition-colors",
      isRequested && "bg-amber-50",
      isBest && !isRequested && "bg-emerald-50",
      isProcessing && !isBest && !isRequested && "bg-slate-100",
      extraCls,
    );
    const borderCls = isBest ? "border-l-4 border-l-emerald-500" : isRequested ? "border-l-4 border-l-amber-400" : "";

    return (
      <tr key={quoteKey(q)} className={rowCls}>
        <td className={cn("px-2 py-2 font-medium text-foreground", borderCls)}>
          <div className="flex items-center gap-1.5">
            <span className="max-w-[150px] truncate" title={q.lenderName ?? undefined}>
              {q.lenderName ?? "—"}
            </span>
            {isProvisionalAcceptance && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
aria-label={`${isProvisionalAcceptance} status`}
                    className="inline-flex text-emerald-500 hover:text-emerald-600"
                  >
                    <CircleDashedCheck className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Lender status is {isProvisionalAcceptance}</TooltipContent>
              </Tooltip>
            )}
            {q.infoMessages && q.infoMessages.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Lender note"
                    className="inline-flex text-amber-500 hover:text-amber-600"
                  >
                    <Info className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] whitespace-normal">
                  {q.infoMessages.join(" ")}
                </TooltipContent>
              </Tooltip>
            )}
            {isProcessing && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Processing status"
                    className="inline-flex text-amber-500 hover:text-amber-600"
                  >
                    <Info className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Lender status is {isProcessing}</TooltipContent>
              </Tooltip>
            )}
            {isBest && (
              <span className="inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold border-emerald-200 bg-emerald-100 text-slate-800">
                Panel #1
              </span>
            )}
            {isRequested && (
              <span className="inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold border-amber-200 bg-amber-100 text-slate-500">
                Requested
              </span>
            )}
          </div>
        </td>
        <td className="px-2 py-2"><FinanceTypeBadge type={q.financeType} /></td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{q.term} mo</td>
        <td className="px-2 py-2 font-semibold text-foreground whitespace-nowrap">{GBP(q.monthlyPayment)}</td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{PCT(q.effectiveRate)}</td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{GBP(q.vehiclePrice)}</td>
        <td className="px-2 py-2 whitespace-nowrap">
          <span
            className={cn(
              "inline-flex items-center gap-1",
              depositExceeds ? "font-medium text-amber-600" : "text-muted-foreground"
            )}
            title={depositExceeds ? "Required deposit exceeds the customer's deposit" : undefined}
          >
            {depositExceeds && <AlertCircle className="size-3.5" />}
            {GBP(q.deposit)}
          </span>
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-muted-foreground underline decoration-dotted underline-offset-2">
                {GBP(pxEquity)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              PX Value {GBP(q.pxValue)} · Settlement {GBP(q.pxSettlement)}
            </TooltipContent>
          </Tooltip>
        </td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{GBP(q.balanceToFinance)}</td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{GBP(q.finalMonthlyPayment)}</td>
        <td className="px-2 py-2 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => onDetails(q)}
              aria-label="Quote details"
              title="View full breakdown"
            >
              <List className="size-4" />
            </Button>
            <Button
              size="sm"
              className="h-7 bg-white border border-slate-300 hover:bg-slate-300 text-[11px] text-slate-700 px-3"
              onClick={() => onPropose(q)}
              disabled={proposingQuoteKey != null || !canPropose}
              title={!canPropose ? "Claim this request to propose to a lender" : undefined}
            >
              {proposingQuoteKey === quoteKey(q) ? (
                <>
                  <Loader2 className="size-3 animate-spin mr-1" />
                  Proposing…
                </>
              ) : (
                "Propose"
              )}
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Quotes" action={headerAction}>
        {errorMessage && (
          <p className="border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600">
            {errorMessage}
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className={th}>Lender</th>
                <th className={th}>Product</th>
                <th className={th}>Term</th>
                <SortHeader label="Monthly" col="Monthly Payment" />
                <SortHeader label="APR" col="APR" />
                <th className={th}>Cash Price</th>
                <th className={th}>Deposit</th>
                <th className={th}>PX Equity</th>
                <th className={th}>Loan</th>
                <th className={th}>Final Monthly</th>
                <th className={th}>Action</th>
              </tr>
            </thead>
            <tbody className="text-[12px]">
              {mainQuotes.length === 0 && belowMainQuotes.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                    No proposable quotes.
                  </td>
                </tr>
              ) : (
                <>
                  {mainQuotes.map((q) => renderRow(q))}
                  {belowMainQuotes.map((q) => renderRow(q, "opacity-80"))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {failedRules.length > 0 && (
        <CollapsibleCard title="Failed Rules" count={failedRules.length}>
          <DecisionTable rows={failedRules} variant="failed" />
        </CollapsibleCard>
      )}

      {declined.length > 0 && (
        <CollapsibleCard title="Declined" count={declined.length}>
          <DecisionTable rows={declined} variant="declined" />
        </CollapsibleCard>
      )}
    </div>
  );
}

/**
 * Shared application detail view. `editable` (queue apps) enables the vehicle /
 * finance / fees edit modals; open-request apps render the same view read-only.
 */
export function UnderwritingQueueApplicationDetail({ editable = false }: { editable?: boolean }) {
  const { applicationId: applicationIdParam } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, workerOptions } = useAppData();

  const navState = location.state as { activity?: UnderwritingActivity; justClaimed?: boolean; origin?: UnderwritingNavOrigin } | null;
  const activity = navState?.activity;
  const applicationId = applicationIdParam ? Number(applicationIdParam) : null;

  // Tracks which right-column tabs have been opened at least once for this application, so
  // their content (and fetched data) stays mounted across tab switches instead of refetching
  // every time — reset only when the user navigates to a different application.
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set(["summary"]));
  useEffect(() => {
    setVisitedTabs(new Set(["summary"]));
  }, [applicationId]);

  const queueClaimResult = useApi(
    (signal) => underwritingService.getQueueClaimByApplicationId(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );
  const isClaimStateKnown =
    !queueClaimResult.loading && queueClaimResult.error == null && queueClaimResult.data != null;

  // Where the user came from, so the breadcrumb + back navigation return to the origin page.
  // On refresh navState is lost, so fall back based on the current route (queue vs dashboard).
  const origin = useMemo<UnderwritingNavOrigin>(
    () =>
      navState?.origin ??
      (location.pathname.startsWith("/dealer-direct-prop-queue")
        ? { path: "/dealer-direct-prop-queue", label: "Dealer Direct Prop Queue" }
        : { path: "/underwriting", label: "Underwriting" }),
    [navState?.origin, location.pathname]
  );

  // Claim handler — assigns the current user to this request.
  // When the page was opened right after claiming from the dashboard, the passed-in
  // activity data is still stale, so trust the `justClaimed` flag to mark it as ours.
  const [lockedBy, setLockedBy] = useState<string | null>(
    navState?.justClaimed ? currentUser?.name ?? null : activity?.claimedBy ?? null
  );
  const [lockedByAgentId, setLockedByAgentId] = useState<number | null>(
    navState?.justClaimed ? currentUser?.userId ?? null : null
  );
  const [lockedOn, setLockedOn] = useState<string | null>(
    navState?.justClaimed ? new Date().toISOString() : null
  );

  useEffect(() => {
    if (queueClaimResult.data) {
      setLockedBy(queueClaimResult.data.claimedBy);
      setLockedByAgentId(queueClaimResult.data.agentId);
      setLockedOn(queueClaimResult.data.claimedOn);
    }
  }, [queueClaimResult.data]);

  // The claim/claim-over race (someone else claiming first) and staleness check are
  // both handled atomically server-side — a 409 here means it's genuinely taken.
  const handleClaim = useCallback(async () => {
    if (!currentUser?.name || applicationId == null) {
      throw new Error("Queue claim state is unavailable");
    }
    const claim = await underwritingService.claimQueue(applicationId, currentUser.userId, currentUser.name);
    setLockedBy(claim.claimedBy);
    setLockedByAgentId(claim.agentId);
    setLockedOn(claim.claimedOn);
  }, [currentUser, applicationId]);

  const handleUnclaim = useCallback(async () => {
    if (applicationId == null) return;
    await underwritingService.deleteQueueClaim(applicationId, "Unclaimed");
    setLockedBy(null);
    setLockedByAgentId(null);
    setLockedOn(null);
  }, [applicationId]);

  // Only the user who claimed the request may action it
  const isClaimedByMe =
    isClaimStateKnown && lockedByAgentId !== null && lockedByAgentId === currentUser?.userId;

  const verifyClaimOwnership = useCallback(async () => {
    if (applicationId == null || currentUser?.userId == null) return false;
    try {
      const claim = await underwritingService.getQueueClaimByApplicationId(applicationId);
      setLockedBy(claim?.claimedBy ?? null);
      setLockedByAgentId(claim?.agentId ?? null);
      setLockedOn(claim?.claimedOn ?? null);
      return claim?.agentId === currentUser.userId;
    } catch {
      setLockedBy(null);
      setLockedByAgentId(null);
      setLockedOn(null);
      return false;
    }
  }, [applicationId, currentUser?.userId]);

  // Prepare-application data drives the whole page: customer (Overview), lender
  // proposals, the latest saved finance figures and the dealer. Vehicle/extras are
  // supplied by the agent via the edit modals.
  const prepareResult = useApi(
    (signal) => underwritingService.getPrepareApplication(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );
  const historyResult = useApi(
    (signal) => underwritingService.getHistory(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  const prepare = prepareResult.data ?? null;
  const [employmentHistory, setEmploymentHistory] = useState<PrepareApplicationEmployment[] | null>(null);
  useEffect(() => {
    if (prepare) setEmploymentHistory(prepare.customer.employmentHistory ?? []);
  }, [prepare]);
  const effectiveEmploymentHistory = useMemo(
    () => employmentHistory ?? prepare?.customer.employmentHistory ?? [],
    [employmentHistory, prepare]
  );
  const mappedProposals = useMemo(() => mapProposals(prepare?.proposals), [prepare]);
  const mappedCustomer = useMemo(
    () => prepare ? mapCustomer({ ...prepare.customer, employmentHistory: effectiveEmploymentHistory }) : null,
    [prepare, effectiveEmploymentHistory]
  );

  // Used for action logic: determines Approved vs Alternative decision and actionedLender fallback.
  const requestedLenderRef = useMemo<QuoteLenderRef | null>(() => {
    const pl = prepare?.customer?.preferredLender;
    if (pl?.lenderId == null) return null;
    return { lenderId: pl.lenderId, financeType: pl.financeType ?? FinanceType.HP } as QuoteLenderRef;
  }, [prepare]);

  // A vehicle selected via VRM search fills the Summary tab and feeds quote generation.
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleDetailModel | null>(null);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);

  // Dealer is seeded from the customer account and can be replaced by the agent.
  const accountDealerId = prepare?.customerAccount?.dealerId ?? null;
  const accountDealerName = prepare?.customerAccount?.affiliateName?.trim() || null;
  const [agentDealer, setAgentDealer] = useState<SelectedDealer | null>(null);
  const [dealerModalOpen, setDealerModalOpen] = useState(false);
  // Dealer search doesn't support ID lookups, so search by name and match on ID.
  const canLookupDealer = accountDealerId != null && accountDealerName != null;
  const dealerLookupResult = useApi(
    (signal) => dealerService.searchDealers(accountDealerName!, signal),
    [accountDealerName],
    { skip: !canLookupDealer }
  );
  const accountDealer = useMemo<SelectedDealer | null>(() => {
    if (accountDealerId == null) return null;
    const match = dealerLookupResult.data?.searchResults.find((d) => d.dealerId === accountDealerId);
    return {
      dealerId: accountDealerId,
      dealerName: match?.dealerName ?? accountDealerName ?? `Dealer #${accountDealerId}`,
      postcode: match?.postcode ?? undefined,
      adminFee: match?.adminFee ?? null,
    };
  }, [accountDealerId, accountDealerName, dealerLookupResult.data]);
  const selectedDealer = agentDealer ?? accountDealer;
  const dealerLoading =
    agentDealer == null && canLookupDealer && !dealerLookupResult.data && !dealerLookupResult.error;

  // Finance figures and fees/add-ons can be overridden from their edit modals.
  const [selectedFinance, setSelectedFinance] = useState<FinanceFiguresInput | null>(null);
  const [hasFinanceUpdate, setHasFinanceUpdate] = useState(false);
  const [financeModalOpen, setFinanceModalOpen] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState<AddOn[] | null>(null);
  // Calculator-ready overrides from the fees modal (feed the next quote generation).
  const [selectedIncludedExtras, setSelectedIncludedExtras] = useState<QuoteSnapshotPayload["includedExtras"]>(null);
  const [selectedAdminFee, setSelectedAdminFee] = useState<number | null>(null);
  const [adminFeeIncludedInFinance, setAdminFeeIncludedInFinance] = useState(true);
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);

  const recovered = useMemo(
    () => prepare && !prepare.quote && historyResult.data ? vehicleFinanceFromHistory(historyResult.data) : null,
    [prepare, historyResult.data]
  );
  const vehicleLookupResult = useApi(
    (signal) => vehicleService.vehicleSearchByVRM(recovered!.vehicle.regNumber, applicationId!, "", undefined, signal),
    [applicationId, recovered?.vehicle.regNumber],
    { skip: applicationId == null || !recovered || selectedVehicle != null }
  );

  // Pre-populate from the quote, or from application history if there is no quote.
  const hasInitializedFromSource = useRef(false);
  useEffect(() => {
    if (hasInitializedFromSource.current || !prepare) return;
    if (!prepare.quote) {
      if (!recovered || selectedVehicle || hasFinanceUpdate) return;
      hasInitializedFromSource.current = true;
      setSelectedFinance(recovered.finance);
      setHasFinanceUpdate(true);
      return;
    }
    hasInitializedFromSource.current = true;
    const q = prepare.quote;
    setSelectedVehicle({
      vehicleId: q.vehicleId,
      regNumber: q.vehicleRegistration,
      make: q.vehicleMake,
      model: q.vehicleModel,
      colour: q.vehicleColour,
      mileage: q.vehicleMileage ?? null,
      price: q.vehiclePrice,
      year: null,
      registrationDate: q.vehicleRegistrationDate
        ? new Date(q.vehicleRegistrationDate).toISOString().slice(0, 10)
        : null,
      vehicleType: null,
    });
    setSelectedFinance({
      cashPrice: q.vehiclePrice,
      deposit: q.deposit,
      partExchange: q.partExchangeValue ?? 0,
      settlement: q.partExchangeSettlement ?? 0,
      loanAmount: q.amountToFinance,
      term: q.term,
      annualMileage: q.annualMileage ?? 8000,
    });
    if (q.dealerFee?.amount) {
      setSelectedAdminFee(q.dealerFee.amount);
      // Older quotes may not carry the flag; keep the previous in-finance default.
      setAdminFeeIncludedInFinance(q.dealerFee.includedInFinance ?? true);
    }
  }, [prepare, recovered, selectedVehicle, hasFinanceUpdate]);

  useEffect(() => {
    if (!recovered || !vehicleLookupResult.data || selectedVehicle) return;
    setSelectedVehicle({
      ...vehicleLookupResult.data,
      mileage: recovered.vehicle.mileage ?? vehicleLookupResult.data.mileage,
    });
  }, [recovered, vehicleLookupResult.data, selectedVehicle]);

  useEffect(() => {
    if (recovered && vehicleLookupResult.error) {
      toast.error(`Could not find ${recovered.vehicle.regNumber}. Add the vehicle manually.`);
    }
  }, [recovered, vehicleLookupResult.error]);

  const hasVehicle = selectedVehicle != null;

  // The agent-selected vehicle seeds the Change/Add Vehicle modal.
  const initialVehicle = selectedVehicle;

  const vehicleDetails = useMemo(
    () =>
      selectedVehicle
        ? vehicleDetailsFromModel(selectedVehicle)
        : vehicleDetailsFromSnapshot(undefined),
    [selectedVehicle]
  );
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<MonthlyPrice[] | null>(null);
  const [proposalsRefreshing, setProposalsRefreshing] = useState(false);
  const [proposingQuoteKey, setProposingQuoteKey] = useState<string | null>(null);
  const [detailsQuote, setDetailsQuote] = useState<MonthlyPrice | null>(null);

  // Clearing quotes brings back the "Generate Quotes" button after an edit.
  const clearQuotes = useCallback(() => {
    setQuotes(null);
    setQuotesError(null);
  }, []);

  const effectiveAdminFee = useMemo(
    () =>
      resolveAdminFee(
        selectedAdminFee != null ? { amount: selectedAdminFee, includedInFinance: adminFeeIncludedInFinance } : null,
        prepare?.quote != null,
        selectedDealer
      ),
    [selectedAdminFee, adminFeeIncludedInFinance, prepare, selectedDealer]
  );

  // Dealer is independent of the vehicle; the admin fee overrides it.
  const dealer = useMemo<Dealer>(
    () => mapDealer(selectedDealer, effectiveAdminFee),
    [selectedDealer, effectiveAdminFee]
  );

  // Effective calculate() vehicles — the agent-selected vehicle mapped to pricing shape.
  const effectiveVehicles = useMemo<Vehicle[]>(() => {
    if (!selectedVehicle) return [];
    const priced = selectedFinance ? { ...selectedVehicle, price: selectedFinance.cashPrice } : selectedVehicle;
    return [mapVehicle(priced, dealer)];
  }, [selectedVehicle, selectedFinance, dealer]);

  const effectiveIncludedExtras = selectedIncludedExtras ?? null;

  const [confirmProposeQuote, setConfirmProposeQuote] = useState<MonthlyPrice | null>(null);
  // Quote awaiting the pre-propose additional questions; cleared once they're submitted.
  const [questionsQuote, setQuestionsQuote] = useState<MonthlyPrice | null>(null);
  const baseFinanceFigures = useMemo(
    () => {
      const figures = financeFiguresFromSnapshot(
        effectiveVehicles[0],
        prepare?.deposit ?? 0,
        prepare?.pxValue ?? 0,
        prepare?.pxSettlement ?? 0,
        prepare?.annualMileage ?? 0,
        prepare?.maxTerm ?? 0,
        dealer
      );
      // Queue applications use the generated quote as the source of truth.
      const quoteLoan = quotes?.find((q) => q.balanceToFinance != null && q.balanceToFinance > 0)?.balanceToFinance;
      if (quoteLoan != null) {
        figures.loanAmount = GBP(quoteLoan);
      }
      return figures;
    },
    [effectiveVehicles, prepare, dealer, quotes]
  );

  const financeFigures = hasFinanceUpdate && selectedFinance
    ? financeFiguresFromInput(selectedFinance)
    : baseFinanceFigures;

  // Numeric seed for the Change Finance Figures modal.
  const financeInput = useMemo<FinanceFiguresInput>(() => {
    if (hasFinanceUpdate && selectedFinance) return selectedFinance;
    const price = selectedVehicle?.price ?? 0;
    const quoteLoan = quotes?.find((q) => q.balanceToFinance != null && q.balanceToFinance > 0)?.balanceToFinance;
    const computedLoan = price - (prepare?.deposit ?? 0) - (prepare?.pxValue ?? 0) + (prepare?.pxSettlement ?? 0);
    if (selectedFinance) {
      return { ...selectedFinance, loanAmount: Math.round(quoteLoan ?? selectedFinance.loanAmount) };
    }
    return {
      cashPrice: price,
      deposit: prepare?.deposit ?? 0,
      partExchange: prepare?.pxValue ?? 0,
      settlement: prepare?.pxSettlement ?? 0,
      loanAmount: Math.round(quoteLoan ?? (computedLoan > 0 ? computedLoan : 0)),
      term: prepare?.maxTerm ?? 0,
      annualMileage: prepare?.annualMileage ?? 0,
    };
  }, [hasFinanceUpdate, selectedFinance, selectedVehicle, prepare, quotes]);

  // Add-ons are agent-supplied: the admin fee plus any chosen extras (names/prices
  // enriched from the generated quotes). Falls back to any activity-provided add-ons.
  const baseAddOns = useMemo(() => {
    const result: AddOn[] = [];
    if ((effectiveAdminFee?.amount ?? 0) > 0) {
      result.push({
        name: "Admin Fee",
        price: formatCurrency(effectiveAdminFee?.amount ?? 0),
        status: effectiveAdminFee?.includedInFinance ? "In Finance" : "Not Included",
      });
    }
    result.push(...addOnsFromSnapshot(selectedIncludedExtras, quotes));
    return result.length ? result : activity?.addOns ?? [];
  }, [effectiveAdminFee, selectedIncludedExtras, quotes, activity]);

  // A change made via the Fees and Add-ons modal overrides the derived list.
  const addOns = selectedAddOns ?? baseAddOns;

  // Current admin fee amount seeds the fees modal.
  const currentAdminFee = useMemo(() => {
    const override = selectedAddOns?.find((a) => a.name === "Admin Fee");
    if (override) return Number(override.price.replace(/[^0-9.]/g, "")) || 0;
    return effectiveAdminFee?.amount ?? 0;
  }, [selectedAddOns, effectiveAdminFee]);

  // Extras already on the quote (excluding the admin fee) preselect the fees modal.
  const currentExtras = useMemo(
    () =>
      addOns
        .filter((a) => a.name !== "Admin Fee")
        .map((a) => ({
          description: a.name,
          payment: (a.status === "In Finance" ? "finance" : "cash") as "finance" | "cash",
          soldByAgentId: a.soldByAgentId,
        })),
    [addOns]
  );

  // Vehicle attributes used to query available extras (from the searched vehicle).
  const extrasVehicleParams = useMemo<VehicleExtraFilterParams | null>(() => {
    const price = selectedFinance?.cashPrice;
    if (selectedVehicle) {
      return {
        make: selectedVehicle.make,
        model: selectedVehicle.model,
        mileage: selectedVehicle.mileage ?? 0,
        price: price ?? selectedVehicle.price,
        registrationDate: selectedVehicle.registrationDate ?? undefined,
        vehicleType: selectedVehicle.vehicleType ?? undefined,
        engine: selectedVehicle.engine ?? undefined,
      };
    }
    return null;
  }, [selectedVehicle, selectedFinance]);

  const handleExtrasConfirm = useCallback(
    ({ adminFee, extras }: { adminFee: number; extras: SelectedExtra[] }) => {
      const next: AddOn[] = [];
      if (adminFee > 0) {
        next.push({
          name: "Admin Fee",
          price: formatCurrency(adminFee),
          status: adminFeeIncludedInFinance ? "In Finance" : "Not Included",
        });
      }
      for (const { option, payment, soldByAgentId, soldByName } of extras) {
        next.push({
          name: option.description,
          price: formatCurrency(option.price),
          status: payment === "finance" ? "In Finance" : "Not Included",
          soldBy: soldByName || undefined,
          soldByAgentId: soldByAgentId ?? undefined,
        });
      }
      setSelectedAddOns(next);
      // Build the calculator-ready extras so the next generation prices them.
      setSelectedIncludedExtras(
        extras.map(({ option, payment, soldByAgentId }) => ({
          productId: option.vehicleExtraOptionId,
          stance:
            payment === "finance"
              ? AncillaryProductLenderStanceType.Finance
              : AncillaryProductLenderStanceType.Cash,
          vehicleExtraTypeId: option.vehicleExtraTypeId,
          vehicleExtraTypeName: option.vehicleExtraTypeName,
          soldByAgentId: soldByAgentId ?? null,
        }))
      );
      setSelectedAdminFee(adminFee);
      clearQuotes();
    },
    [clearQuotes, adminFeeIncludedInFinance]
  );

  // Extras chosen via the modal already carry names/prices, so nothing is pending.
  const addOnsPending = false;

  const currentEmploymentYears = useMemo(() => {
    const current = effectiveEmploymentHistory.find((employment) => employment.isCurrentEmployment) ?? effectiveEmploymentHistory[0];
    return current?.yearsAtEmployment ?? null;
  }, [effectiveEmploymentHistory]);

  // Action state — a proposal determines the (locked) decision when the popup opens
  const [actionOpen, setActionOpen] = useState(false);
  const [decisionsOpen, setDecisionsOpen] = useState(false);
  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(false);
  const [creditCheckOpen, setCreditCheckOpen] = useState(false);
  const [hasProposed, setHasProposed] = useState(false);
  const [hasActioned, setHasActioned] = useState(false);

  const handlePropose = useCallback(
    async (quote: MonthlyPrice) => {
      if (!prepare || applicationId == null || effectiveVehicles.length === 0) return;
      if (!(await verifyClaimOwnership())) {
        toast.error("Only the agent who claimed this request can propose to a lender.");
        return;
      }
      setProposingQuoteKey(quoteKey(quote));
      const toastId = toast.loading(`Proposing to ${quote.lenderName || "lender"}…`);
      try {
        const quoteRequest = mapMonthlyPriceToQuoteRequest(
          quote,
          applicationId,
          effectiveVehicles[0],
          dealer,
          currentUser?.userId ?? null,
          selectedIncludedExtras
        );
        const savedQuote = await proposalService.createQuote(quoteRequest);
        const loanProposal = buildLoanProposalRequest(savedQuote);
        await proposalService.sendLoanProposal(loanProposal, savedQuote);
        setHasProposed(true);
        toast.success(`Proposed to ${quote.lenderName || "lender"} successfully`, { id: toastId });
      } catch {
        toast.error("Failed to propose quote to lender. Please try again.", { id: toastId });
      } finally {
        setProposingQuoteKey(null);
      }
    },
    [verifyClaimOwnership, prepare, applicationId, effectiveVehicles, dealer, selectedIncludedExtras, currentUser, requestedLenderRef]
  );

  // Additional questions are only required when proposing to a prime lender;
  // non-prime proposals skip straight to the confirmation dialog.
  const handleRequestPropose = useCallback((quote: MonthlyPrice) => {
    setDetailsQuote(null);
    const proposal = prepare?.proposals.find((candidate) => candidate.lenderId === quote.lenderId);
    if (proposal?.isPrime) {
      setQuestionsQuote(quote);
    } else {
      setConfirmProposeQuote(quote);
    }
  }, [prepare]);

  const handleQuestionsSubmitted = useCallback(() => {
    setQuestionsQuote((quote) => {
      setConfirmProposeQuote(quote);
      return null;
    });
  }, []);

  const handleConfirmPropose = useCallback(() => {
    const quote = confirmProposeQuote;
    setConfirmProposeQuote(null);
    if (quote) handlePropose(quote);
  }, [confirmProposeQuote, handlePropose]);

  // Runs once the queue action has been recorded (which closes the claim server-side
  // in the same transaction) — adds the audit note and leaves the page.
  const finalizeAction = useCallback(
    async (notes: string) => {
      if (!activity) return;
      try {
        await underwritingService.addNote({
          applicationId: activity.applicationId,
          comment: `Dealer Direct Queue action by ${currentUser?.name ?? "Unknown"}.${notes ? `: ${notes}` : ""}`,
          createdBy: currentUser?.name ?? "",
          isImportant: false,
          isPublic: false,
          isUserComment: true,
          userId: currentUser?.userId ?? 0,
        });
      } catch {
        toast.warning("Action submitted, but the note could not be added.");
      }

      setHasActioned(true);
      toast.success("Action submitted successfully");
      navigate(origin.path);
    },
    [activity, currentUser, navigate, origin]
  );

  /**
   * Writes the queue audit row the dashboard reports on, which closes the claim
   * server-side in the same transaction. If this fails, we must NOT touch the claim
   * or leave the page — the underlying allocate/dead change already happened, but
   * nothing here is considered "actioned" until this write lands. The caller
   * re-throws so the dialog stays open and the agent can retry or escalate.
   * Only queue applications are recorded — an open request has no queue entry, so a
   * row here would have no turnaround to measure and would skew the totals.
   */
  const recordQueueAction = useCallback(
    async (payload: UWQueueActionPayload): Promise<void> => {
      if (!editable) return;
      if (!(await verifyClaimOwnership())) {
        throw new Error("Queue claim is not owned by the current user");
      }
      await underwritingService.createQueueAction(payload);
    },
    [editable, verifyClaimOwnership]
  );

  const handleAllocateAction = useCallback(
    async ({ agentId, agentName }: AllocateActionPayload) => {
      if (applicationId == null) return;
      // Mirrors Flex: an application with no agent yet is moved to Initial Contact.
      const allocatedAgentId = prepare?.customer?.allocationData?.allocatedAgentId ?? null;
      await applicationService.allocateToAgent({
        loanApplicationId: applicationId,
        agentId,
        changeToInitial: !allocatedAgentId,
        allocationReason: null,
        stateChangeReasonId: null,
        isCallback: false,
        createdByUserId: currentUser?.userId ?? null,
      });
      await recordQueueAction({
        applicationId,
        actionType: "Allocated",
        actionedByUserId: currentUser?.userId ?? 0,
        actionedByName: currentUser?.name ?? "",
        allocatedOn: activity?.createdOn ?? new Date().toISOString(),
        targetAgentId: agentId,
        targetAgentName: agentName,
      });
      await finalizeAction(`Allocated to ${agentName}.`);
    },
    [applicationId, prepare, currentUser, activity, recordQueueAction, finalizeAction]
  );

  const handleDeadAction = useCallback(
    async ({ statusTypeId, statusType, statusReasonId, statusReason, note, isImportant }: DeadActionPayload) => {
      if (applicationId == null) return;
      if (note) {
        await underwritingService.addNote({
          applicationId,
          comment: note,
          createdBy: currentUser?.name ?? "",
          isImportant,
          isPublic: false,
          isUserComment: true,
          userId: currentUser?.userId ?? 0,
        });
      }
      await applicationService.moveApplication({
        applicationId,
        statusTypeId,
        statusType,
        statusReasonId,
      });
      await recordQueueAction({
        applicationId,
        actionType: "Dead",
        actionedByUserId: currentUser?.userId ?? 0,
        actionedByName: currentUser?.name ?? "",
        allocatedOn: activity?.createdOn ?? new Date().toISOString(),
        statusTypeId,
        statusReasonId,
        notes: note || null,
      });
      await finalizeAction(note || `Application marked as ${statusType} — ${statusReason}.`);
    },
    [applicationId, currentUser, activity, recordQueueAction, finalizeAction]
  );

  // Auto-redirect to the dashboard if the application stays open too long without an action
  useEffect(() => {
    if (hasActioned) return;
    const timer = window.setTimeout(() => {
      toast.info("Application closed after 15 minutes of inactivity.");
      navigate(origin.path);
    }, INACTIVITY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [hasActioned, navigate, origin]);

  // Warn before leaving after a proposal if no action has been recorded yet
  const shouldWarnOnLeave = hasProposed && !hasActioned;

  useEffect(() => {
    if (!shouldWarnOnLeave) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldWarnOnLeave]);

  const confirmLeave = useCallback(
    () => !shouldWarnOnLeave || window.confirm(LEAVE_WITHOUT_ACTION_MESSAGE),
    [shouldWarnOnLeave]
  );

  const handleGenerateQuotes = useCallback(async () => {
    if (!prepare || !mappedCustomer) {
      setQuotesError("No application data available for this application.");
      return;
    }
    if (effectiveVehicles.length === 0) {
      setQuotesError("Add a vehicle to generate quotes.");
      return;
    }
    setQuotesLoading(true);
    setQuotesError(null);
    setQuotes(null);
    try {
      const priceSource = {
        affiliateId: prepare.customer.affiliateUserId ?? null,
        affiliateType: prepare.customerAccount?.affiliateUserType
          ? prepare.customerAccount.affiliateUserType === "Car Dealer"
            ? AffiliateType.Dealer
            : AffiliateType.Affiliate
          : null,
        isPrime: false,
        isSearch: false,
        poweredByLenderId: prepare.customer.poweredBy?.financeCompanyId ?? null,
        useHardcodedResiduals: false,
      };
      const result = await calculate(
        effectiveVehicles,
        mappedProposals,
        mappedCustomer,
        dealer,
        selectedFinance?.term ?? prepare.maxTerm ?? 0,
        selectedFinance?.deposit ?? prepare.deposit ?? 0,
        selectedFinance?.partExchange ?? prepare.pxValue ?? 0,
        selectedFinance?.settlement ?? prepare.pxSettlement ?? 0,
        selectedFinance?.annualMileage ?? prepare.annualMileage ?? 0,
        effectiveIncludedExtras,
        priceSource
      );
      setQuotes(result);
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err !== null && "message" in err
          ? (err as { message: string }).message
          : "Failed to generate quotes. Please try again.";
      setQuotesError(message);
    } finally {
      setQuotesLoading(false);
    }
  }, [prepare, mappedCustomer, mappedProposals, dealer, effectiveVehicles, effectiveIncludedExtras, selectedFinance]);

  // Auto-generate quotes once a vehicle is present and the dealer has resolved.
  const autoGeneratedRef = useRef(false);
  useEffect(() => {
    if (prepare && effectiveVehicles.length > 0 && !dealerLoading && !autoGeneratedRef.current) {
      autoGeneratedRef.current = true;
      handleGenerateQuotes();
    }
  }, [prepare, effectiveVehicles, dealerLoading, handleGenerateQuotes]);

  const waitingForProposalRefreshRef = useRef(false);
  const handleRefreshQuotesAndProposals = useCallback(() => {
    if (!prepare || !hasVehicle || proposalsRefreshing || quotesLoading) return;
    waitingForProposalRefreshRef.current = false;
    setProposalsRefreshing(true);
    prepareResult.refetch();
  }, [prepare, hasVehicle, proposalsRefreshing, quotesLoading, prepareResult.refetch]);

  useEffect(() => {
    if (!proposalsRefreshing) return;
    if (prepareResult.loading) {
      waitingForProposalRefreshRef.current = true;
      return;
    }
    if (!waitingForProposalRefreshRef.current) return;
    waitingForProposalRefreshRef.current = false;
    setProposalsRefreshing(false);
    if (prepareResult.error) {
      setQuotesError(`Could not refresh proposals: ${prepareResult.error}`);
      return;
    }
    handleGenerateQuotes();
  }, [proposalsRefreshing, prepareResult.loading, prepareResult.error, handleGenerateQuotes]);

  const handleDashboardClick = () => {
    if (!confirmLeave()) return;
    navigate(origin.path);
  };

  if (prepareResult.loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="size-5 animate-spin" />
        Loading application…
      </div>
    );
  }

  // No activity row means the user navigated directly (e.g. refreshed the page)
  if (!activity) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-muted-foreground">
        <p className="text-lg font-medium">Application not found</p>
        <p className="text-sm">Please navigate from the dashboard.</p>
        <Button variant="outline" onClick={handleDashboardClick}>
          ← Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      style={{ "--foreground": "#334155" } as CSSProperties}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <Breadcrumb customerName={activity.customerName ?? "—"} originLabel={origin.label} onDashboardClick={handleDashboardClick} />
        <div className="flex items-center gap-2">
          <HeaderClaimButton
            lockedBy={lockedBy}
            lockedByAgentId={lockedByAgentId}
            lockedOn={lockedOn}
            currentUserId={currentUser?.userId ?? null}
            disabled={!isClaimStateKnown}
            onClaim={handleClaim}
            onUnclaim={handleUnclaim}
          />
          <Button
            className="border-purple-600 border bg-purple-600 hover:bg-purple-700 text-white text-xs px-2.5 py-1.5 h-auto"
            onClick={() => setActionOpen(true)}
            disabled={!isClaimedByMe}
            title={!isClaimedByMe ? "Claim this request to action it" : undefined}
          >
            Action
          </Button>
        </div>
      </div>

      {!isClaimedByMe && (
        <div role="alert" className="shrink-0 border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-800">
          Claim this application to unlock full functionality.
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-auto xl:overflow-hidden bg-muted/40">
        <div className="mx-auto grid w-full max-w-[1600px] grid-cols-1 gap-6 p-6 xl:grid-cols-[minmax(0,1fr)_620px] xl:h-full xl:min-h-0">
          {/* Left column */}
          <div className="flex min-w-0 flex-col gap-4 xl:min-h-0 xl:overflow-y-auto">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{activity.customerName}</h1>
              <span className="text-sm text-muted-foreground">{formatTimeAgo(activity.createdOn)}</span>
            </div>

            {prepareResult.error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                Could not load application data: {prepareResult.error}
              </div>
            )}

            {prepare && !prepare.quote && !historyResult.loading && !historyResult.error && historyResult.data && !recovered && !selectedVehicle && (
              <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Can't find a vehicle for the current application. Please check application history
              </div>
            )}

            {questionsQuote != null ? (
              <AdditionalQuestionsPage
                open={questionsQuote != null}
                onOpenChange={(o) => {
                  if (!o) setQuestionsQuote(null);
                }}
                applicationId={applicationId}
                lenderName={questionsQuote?.lenderName ?? null}
                showVwfs={questionsQuote?.lenderId === FinanceCompany.VWFS}
                employmentYears={currentEmploymentYears}
                onSubmitted={handleQuestionsSubmitted}
              />
            ) : (
              <>
                <SectionCard title="Overview" action={
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => setCreditCheckOpen(true)} disabled={applicationId == null || !prepare || !isClaimedByMe} title={!isClaimedByMe ? "Claim this application to run a credit check" : undefined}>
                      <ScanSearch className="size-3.5" />
                      <span className="text-xs">Credit Report</span>
                    </Button>
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => setCustomerDetailsOpen(true)} disabled={applicationId == null || !prepare || !isClaimedByMe} title={!isClaimedByMe ? "Claim this application to update customer details" : undefined}>
                      <p className="text-xs">Update Customer</p>
                    </Button>
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => setDecisionsOpen(true)} disabled={applicationId == null || !isClaimedByMe} title={!isClaimedByMe ? "Claim this application to view or add decisions" : undefined}>
                      <p className="text-xs">View/Add Decisions</p>
                    </Button>
                  </div>
                }>
                  {customerDetailsOpen && prepare && applicationId != null ? (
                    <CustomerDetailsEditor
                      applicationId={applicationId}
                      customer={prepare.customer}
                      customerAccount={prepare.customerAccount}
                      vehicleTypeId={selectedVehicle?.vehicleType ?? null}
                      onCancel={() => setCustomerDetailsOpen(false)}
                      onSaved={() => {
                        setCustomerDetailsOpen(false);
                        prepareResult.refetch();
                      }}
                    />
                  ) : (
                    <OverviewContent
                      details={prepare?.customer ?? null}
                      loading={prepareResult.loading}
                      quotesCount={quotes?.length ?? 0}
                      activity={activity}
                      amountToFinance={
                        hasFinanceUpdate
                          ? selectedFinance?.loanAmount
                          : quotes?.find((q) => q.balanceToFinance != null && q.balanceToFinance > 0)?.balanceToFinance
                      }
                    />
                  )}
                </SectionCard>

                {applicationId != null && (
                  <EmploymentHistory
                    applicationId={applicationId}
                    employmentHistory={effectiveEmploymentHistory}
                    canEdit={isClaimedByMe}
                    onChange={setEmploymentHistory}
                  />
                )}

                {quotes ? (
                  <QuotesTable
                    quotes={quotes}
                    requestedLender={null}
                    bestLender={null}
                    requestedDeposit={selectedFinance?.deposit ?? prepare?.deposit ?? 0}
                    onPropose={handleRequestPropose}
                    onDetails={setDetailsQuote}
                    proposingQuoteKey={proposingQuoteKey}
                    canPropose={isClaimedByMe}
                    errorMessage={quotesError}
                    headerAction={
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={handleRefreshQuotesAndProposals}
                        disabled={!prepare || !hasVehicle || quotesLoading || proposalsRefreshing || prepareResult.loading}
                        title={!hasVehicle ? "Add a vehicle to generate quotes" : undefined}
                      >
                        {(proposalsRefreshing || prepareResult.loading) ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            <p className="text-xs text-muted-foreground">Refreshing proposals…</p>
                          </>
                        ) : (
                          <>
                            <RefreshCw className="size-3.5 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground">Refresh quotes/proposals</p>
                          </>
                        )}
                      </Button>
                    }
                  />
                ) : (
                  <SectionCard title="Quotes">
                    {quotesLoading && (
                      <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                        <Loader2 className="size-4 animate-spin" />
                        Generating quotes…
                      </div>
                    )}
                    {quotesError && (
                      <p className="px-4 py-3 text-sm font-medium text-rose-600">{quotesError}</p>
                    )}
                    {!quotesLoading && (
                      <div className="flex flex-col items-center gap-3 py-10">
                        <p className="text-sm text-muted-foreground">
                          Quotes are generated at point of review to ensure figures are current.
                        </p>
                        <Button
                          className="bg-purple-600 hover:bg-purple-700 text-white px-8 gap-2"
                          onClick={handleGenerateQuotes}
                          disabled={!prepare || !hasVehicle}
                          title={!hasVehicle ? "Add a vehicle to generate quotes" : undefined}
                        >
                          Generate Quotes
                        </Button>
                      </div>
                    )}
                  </SectionCard>
                )}
              </>
            )}
          </div>

          {/* Right column: tabs and persistent history */}
          <div className="grid min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm md:h-[600px] md:grid-cols-2 md:grid-rows-[minmax(0,1fr)] md:divide-x md:divide-border xl:h-full xl:min-h-0">
            <Tabs
              defaultValue="summary"
              variant="underline"
              className="flex h-[600px] min-w-0 flex-col gap-0 md:h-full"
              onValueChange={(value) =>
                setVisitedTabs((prev) => (prev.has(value) ? prev : new Set(prev).add(value)))
              }
            >
              <TabsList className="h-11 w-full shrink-0 justify-between gap-1 border-b border-border px-2">
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="notes">Notes</TabsTrigger>
                <TabsTrigger value="messages">Messages</TabsTrigger>
                <TabsTrigger value="transcripts">Transcripts</TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="min-h-0 flex-1 overflow-y-auto">
                <SummaryTab
                  vehicle={vehicleDetails}
                  dealer={selectedDealer}
                  dealerLoading={dealerLoading}
                  finance={financeFigures}
                  addOns={addOns}
                  addOnsPending={addOnsPending}
                  addOnsLoading={quotesLoading}
                  hasVehicle={hasVehicle}
                  canEdit={isClaimedByMe}
                  onEditVehicle={editable ? () => setVehicleModalOpen(true) : undefined}
                  onEditDealer={editable ? () => setDealerModalOpen(true) : undefined}
                  onEditFinance={editable ? () => setFinanceModalOpen(true) : undefined}
                  onEditExtras={editable ? () => setExtrasModalOpen(true) : undefined}
                />
              </TabsContent>
              <TabsContent
                value="notes"
                forceMount={visitedTabs.has("notes") || undefined}
                className="h-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
              >
                <NotesTab
                  applicationId={applicationId}
                  currentUserName={currentUser?.name ?? null}
                  currentUserId={currentUser?.userId ?? null}
                  onNoteAdded={historyResult.refetch}
                />
              </TabsContent>
              <TabsContent
                value="messages"
                forceMount={visitedTabs.has("messages") || undefined}
                className="h-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
              >
                <MessagesTab applicationId={applicationId} />
              </TabsContent>
              <TabsContent
                value="transcripts"
                forceMount={visitedTabs.has("transcripts") || undefined}
                className="h-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
              >
                <TranscriptsTab applicationId={applicationId} />
              </TabsContent>
            </Tabs>
            <section aria-label="History" className="flex h-[300px] min-w-0 flex-col border-t border-border md:h-full md:min-h-0 md:border-t-0">
              <div className="flex h-11 shrink-0 items-center justify-between border-b border-border px-4">
                <h2 className="text-sm font-medium text-foreground">History</h2>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={historyResult.refetch}
                  disabled={historyResult.loading}
                  aria-label="Refresh history"
                  title="Refresh history"
                >
                  <RefreshCw className={cn("size-3.5", historyResult.loading && "animate-spin")} />
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <HistoryTab historyResult={historyResult} />
              </div>
            </section>
          </div>
        </div>
      </div>

      {decisionsOpen && applicationId != null && (
        <LenderDecisionHistoryDialog
          key={applicationId}
          applicationId={applicationId}
          onOpenChange={setDecisionsOpen}
          canEdit={isClaimedByMe}
          verifyClaimOwnership={verifyClaimOwnership}
          onDecisionsChanged={() => {
            clearQuotes();
            prepareResult.refetch();
            historyResult.refetch();
          }}
        />
      )}

      <QueueActionDialog
        open={actionOpen}
        onOpenChange={setActionOpen}
        applicationId={activity.applicationId}
        onAllocate={handleAllocateAction}
        onMarkDead={handleDeadAction}
      />

      <CreditCheckDialog
        open={creditCheckOpen}
        onOpenChange={setCreditCheckOpen}
        applicationId={applicationId}
      />

      <QuoteDetailsDialog
        open={detailsQuote != null}
        onOpenChange={(o) => {
          if (!o) setDetailsQuote(null);
        }}
        quote={detailsQuote}
        onPropose={handleRequestPropose}
        proposing={detailsQuote != null && proposingQuoteKey === quoteKey(detailsQuote)}
        canPropose={isClaimedByMe}
      />

      {editable && (
        <>
          {applicationId != null && (
            <AddVehicleModal
              open={vehicleModalOpen}
              onOpenChange={setVehicleModalOpen}
              applicationId={applicationId}
              mode={hasVehicle ? "change" : "add"}
              initialVehicle={initialVehicle}
              onConfirm={(v) => {
                setSelectedVehicle(v);
                clearQuotes();
              }}
            />
          )}

          <ChangeDealerModal
            open={dealerModalOpen}
            onOpenChange={setDealerModalOpen}
            initialDealer={selectedDealer}
            onConfirm={(d) => {
              setAgentDealer(d);
              clearQuotes();
            }}
          />

          <ChangeFinanceModal
            open={financeModalOpen}
            onOpenChange={setFinanceModalOpen}
            initial={financeInput}
            onConfirm={(f) => {
              setSelectedFinance(f);
              setHasFinanceUpdate(true);
              clearQuotes();
            }}
          />

          <ChangeExtrasModal
            open={extrasModalOpen}
            onOpenChange={setExtrasModalOpen}
            vehicle={extrasVehicleParams}
            initialAdminFee={currentAdminFee}
            agentOptions={workerOptions}
            initialExtras={currentExtras}
            onConfirm={handleExtrasConfirm}
          />
        </>
      )}

      <Dialog
        open={confirmProposeQuote != null}
        onOpenChange={(o) => {
          if (!o) setConfirmProposeQuote(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Propose to lender</DialogTitle>
            <DialogDescription>
              Do you want to propose to {confirmProposeQuote?.lenderName || "this lender"}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 sm:gap-3">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleConfirmPropose}
            >
              Propose
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Detail page for Underwriting Queue applications — the summary is editable. */
export function UnderwritingQueueApplicationDetailPage() {
  return <UnderwritingQueueApplicationDetail editable />;
}
